import CssBaseline from "@mui/material/CssBaseline"
import GlobalStyles from "@mui/material/GlobalStyles"
import { createRoot } from "react-dom/client"
import { enqueueSnackbar } from "app/components/SnackbarProvider"
import { FlashMessages } from "app/components/FlashMessages"
import * as React from "react"
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { ErrorBoundary } from "react-error-boundary"
import { ClientErrorPage } from "app/components/ErrorPage"
import { ConnectedUser } from "./domains/user/model"
import { setDefaultOptions } from "date-fns/setDefaultOptions"
import { fr } from "date-fns/locale/fr"
import { router } from "common/routing/router"
import { AsyncClient, internalClient } from "./api"
import { config } from "./config"
import { AppProviders } from "./components/AppProviders"

setDefaultOptions({ locale: fr })

interface AppProps {
  component: React.ElementType
  user: ConnectedUser
  props: PageProps
  nav: NavItem | null
  flashMessages: FlashMessages
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },

  queryCache: new QueryCache({
    onError(error, query) {
      console.error("[React Query] Query failed", {
        queryKey: query.queryKey,
        error,
      })
    },
  }),

  mutationCache: new MutationCache({
    onSuccess(_data, _variables, _context, mutation) {
      if (mutation.meta?.successMessage) {
        enqueueSnackbar({
          message: mutation.meta.successMessage,
          variant: "success",
        })
      }
    },

    onError(error, _variables, _onMutateResult, mutation, _context) {
      console.error(error)

      if (mutation.meta?.errorMessage) {
        enqueueSnackbar({
          message: mutation.meta.errorMessage,
          variant: "error",
        })
      }
    },
  }),
})

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      successMessage?: string
      errorMessage?: string
    }
  }
}

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient

const asyncClient = new AsyncClient(router)

const App = ({
  component: Component,
  props,
  user,
  nav,
  flashMessages,
}: AppProps) => {
  return (
    <AppProviders
      queryClient={queryClient}
      apiClient={internalClient}
      asyncClient={asyncClient}
      connectedUser={user}
      nav={nav}
      googleMapsApiKey={config.GMAP_API_KEY}
    >
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: {
            scrollBehavior: "smooth",
            scrollPaddingTop: 60,
          },
        }}
      />
      <FlashMessages messages={flashMessages} />
      <ErrorBoundary FallbackComponent={ClientErrorPage}>
        <Component {...props} />
      </ErrorBoundary>
    </AppProviders>
  )
}

interface PageParams {
  component: React.ElementType
  props?: PageProps
  rootElementId?: string
  wrapper?: React.ElementType
  user?: ConnectedUser | null
  nav?: NavItem | null
  flashMessages?: FlashMessages | null
}

export function renderPage({
  component: Component,
  props = window.pageProps || {},
  rootElementId = "app",
  wrapper: Wrapper = App,
  user = window.user || null,
  nav = window.nav || null,
  flashMessages = window.flashMessages || null,
}: PageParams) {
  const appElement = document.getElementById(rootElementId)
  const locale = document.documentElement.lang

  if (!appElement) {
    throw new Error(`Element with id="${rootElementId}" doesn't exist`)
  }

  const root = createRoot(appElement)

  root.render(
    <Wrapper
      component={Component}
      locale={locale}
      props={props}
      user={user}
      nav={nav}
      flashMessages={flashMessages}
    />,
  )
}
